import cliTruncate from "cli-truncate"
import Cookies from "js-cookie"
import { html, render } from "lit-html"
import { repeat } from "lit-html/directives/repeat"
import { virtual, useState } from "haunted"

import { HeaderSection, LocationInfo, DateTimeInfo } from "./components"

import AssignmentAppButtonForm from "../forms/assignment-app"
import { addToastMessage } from "../generic/messages"
import {
  ANY_AVAILABLE,
  ROLE_ASSIGN_PATH,
  ROLE_ICON_MAP,
  RELEASE_ASSIGNMENT_API,
  STATUSES,
} from "../constants"
import { keyClickWrapper, slugify } from "../utils"
import markdown from "../utils/markdown"

const csrfToken = Cookies.get("csrftoken")

const releaseAssignment = (e, assignmentUrl) => {
  const $assignmentRow = e.target.closest(".assignment")
  if (!$assignmentRow) return

  const ariaControls = $assignmentRow.getAttribute("aria-controls")
  const releasePath = `${assignmentUrl}${RELEASE_ASSIGNMENT_API}`
  fetch(releasePath, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
    body: JSON.stringify({}),
  })
    .then((res) => {
      if (!res.ok) {
        res.text().then((text) => {
          throw new Error(text)
        })
      }

      res.json().then((data) => {
        const mount = document.createElement("div")
        render(AssignmentRow(data), mount)
        $assignmentRow.replaceWith(...mount.childNodes)
        setTimeout(() => {
          const $assignmentRow = document.querySelector(
            `[aria-controls="${ariaControls}"]`
          )
          $assignmentRow?.click()
        }, 0)
        addToastMessage("Assignment released")
      })
    })
    .catch((err) => {
      console.error(err)
      addToastMessage(
        "There was an error when releasing this assignment",
        "error"
      )
    })
}

const getAppByCategory = (applications, category) => {
  return (
    applications?.filter(
      ({ categories: cArr }) =>
        cArr?.includes(category) || cArr?.includes(ANY_AVAILABLE)
    ) || []
  )
}

const IconDetailBlock = ({ roles, categories, applications = [] }) => {
  if (Object.keys(categories).length == 0) return ``

  return html`
    <div class="mt-5 flex flex-wrap">
      ${repeat(
        Object.entries(categories),
        ([category]) => category,
        ([category, label]) => {
          const unassignedRoles = roles.filter(
            ({ category: c, user }) =>
              c === category && (user === null || user === undefined)
          )
          if (unassignedRoles.length === 0) return ``

          const roleCountDisplay =
            unassignedRoles.length > 1 ? `(${unassignedRoles.length}) ` : ""

          const categoryApps = getAppByCategory(applications, category)
          const applicantCount = categoryApps.length
          const applicantCountDisplay = applicantCount
            ? ` - ${applicantCount} applicant${applicantCount === 1 ? "" : "s"}`
            : ""

          return html`
            <div
              class="mr-4 mb-2 rounded-md border-px border-gray-border px-4 py-2 text-gray-interactive text-sm"
            >
              <i class="mr-4 ${ROLE_ICON_MAP[category] || "fas fa-file"}"></i>
              <span> ${roleCountDisplay}${label}${applicantCountDisplay} </span>
            </div>
          `
        }
      )}
    </div>
  `
}

const RolesList = ({ roles, categories, applications }) => {
  if (!roles || roles.length === 0) {
    return html`<p>No roles have been added yet.</p>`
  }

  const renderRoleStatus = (status, user) => {
    if (!user?.id) return ``
    const slugifiedStatus = slugify(status)
    return html`
      <span
        class="ml-4 border-px border-role-status-border-${slugifiedStatus} bg-role-status-bg-${slugifiedStatus} rounded-md px-3 py-2 text-tiny text-role-status-${slugifiedStatus} font-bold"
      >
        ${status}
      </span>
    `
  }

  const renderUserInfo = (user, categoryApps) => {
    if (user?.id) {
      return html`<a href="${user.url}">${user.full_name || user.name}</a>`
    }
    return html`Unassigned
    ${categoryApps.length
      ? `(${categoryApps.length} applicant${
          categoryApps.length === 1 ? "" : "s"
        })`
      : ``}`
  }

  const renderRoleSummary = (status, summary) =>
    status === STATUSES.APPROVED && summary
      ? html`<p class="mt-2 py-4 text-black">${summary}</p>`
      : ``

  return html`
    <div>
      ${repeat(
        roles,
        ({ id }) => id,
        ({
          url: roleUrl,
          category,
          user,
          status,
          reporting_url: reportingUrl,
          summary,
        }) => {
          const categoryApps = getAppByCategory(applications, category)

          return html`
            <div class="flex mt-6">
              <a
                href="${roleUrl}"
                class="shrink-0 h-10 w-10 mr-5 flex items-center justify-center rounded-lg ${user?.id ||
                categoryApps.length
                  ? "hover:border-2 hover:border-gray-450 bg-yellow text-black"
                  : "border-px hover:border-2 hover:border-gray-450 text-gray-450"}"
              >
                <i class="${ROLE_ICON_MAP[category] || "fas fa-file"}"></i>
              </a>
              <div class="grow">
                <div class="-mt-2 w-full md:flex justify-between items-center">
                  <div>
                    <span class="block text-black">
                      ${categories?.[category] || ""}
                      ${renderRoleStatus(status, user)}
                    </span>
                    <span class="block mt-2 text-gray-interactive text-sm">
                      ${renderUserInfo(user, categoryApps)}
                    </span>
                  </div>
                  <div class="flex justify-start md:justify-end">
                    ${reportingUrl
                      ? html`
                          <a
                            href="${reportingUrl}"
                            class="pr-6 md:pl-6 py-2 md:py-6 inline-block text-sm font-bold"
                          >
                            Reporting
                          </a>
                        `
                      : ``}
                    <a
                      href="${roleUrl}"
                      class="pr-6 md:pl-6 py-2 md:py-6 inline-block text-sm font-bold"
                    >
                      View
                    </a>
                  </div>
                </div>
                ${renderRoleSummary(status, summary)}
              </div>
            </div>
          `
        }
      )}
    </div>
  `
}

const RoleManagementSection = ({
  url,
  status,
  roles,
  categories,
  applications,
}) => {
  const hasRoles = roles && roles.length > 0

  return html`
    <div class="mt-5 border-t-px border-gray-border pt-5">
      <div class="mb-5 w-full flex justify-between items-center">
        <div class="text-base font-bold">Roles</div>
        <div class="flex items-center">
          <a
            href="${url}${ROLE_ASSIGN_PATH}"
            class="ml-4 button tetiary h-9 px-5"
          >
            Manage roles
          </a>
          ${status === STATUSES.DRAFT && hasRoles
            ? html`
                <button
                  class="ml-4 button primary h-9 px-5"
                  @click=${(e) => releaseAssignment(e, url)}
                >
                  Open for application
                </button>
              `
            : ``}
          ${status === STATUSES.OPEN && hasRoles
            ? html` <a
                href="${url}${ROLE_ASSIGN_PATH}"
                class="ml-4 button primary h-9 px-5"
              >
                Assign roles
              </a>`
            : ``}
        </div>
      </div>
      ${RolesList({ roles, categories, applications })}
    </div>
  `
}

const RoleAppList = ({ roles, categories, applications }) => {
  if (!roles || roles.length === 0) {
    return html`<p>No roles have been added yet.</p>`
  }

  return html`
    <div>
      ${repeat(
        roles,
        ({ id }) => id,
        ({ category }) => {
          const categoryApps = getAppByCategory(applications, category)

          return html`
            <div class="mt-6 flex items-center">
              <span
                class="shrink-0 h-10 w-10 mr-5 flex items-center justify-center rounded-lg border-px text-gray-450"
              >
                <i class="${ROLE_ICON_MAP[category] || "fas fa-file"}"></i>
              </span>
              <div class="grow">
                <span class="block text-black">
                  ${categories?.[category] || ""}
                </span>
                <span class="block mt-2 text-gray-interactive text-sm">
                  ${categoryApps.length}
                  applicant${categoryApps.length === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          `
        }
      )}
    </div>
  `
}

const RoleApplySection = ({ app_form, roles, categories, applications }) => {
  return html`
    <div class="mt-5 border-t-px border-gray-border pt-5">
      <div class="mb-5 w-full flex justify-between items-center">
        <div class="text-base font-bold">Roles</div>
        ${AssignmentAppButtonForm({
          ...app_form,
          className: "primary mb-4 grow-0",
        })}
      </div>
      ${RoleAppList({ roles, categories, applications })}
    </div>
  `
}

const AssignmentRow = virtual(
  ({
    id,
    name,
    url,
    status,
    description,
    agency,
    start_date,
    start_time,
    end_time,
    shortened_timezone,
    tags,
    remote,
    link,
    location,
    gcal_link,
    roles,
    applications,
    can_edit,
    can_clone,
    app_form,
  }) => {
    const [expanded, setExpanded] = useState(false)
    const [showFullDescription, setShowFullDescription] = useState(false)

    const $filterMount = document.getElementById("filter-mount")
    const iconIcalSrc = $filterMount.dataset.iconIcal
    const iconGoogleCalendarSrc = $filterMount.dataset.iconGoogleCalendar

    const categories = roles.reduce(
      (accumulator, { category, label }) => ({
        ...accumulator,
        [category]: label,
      }),
      {}
    )
    const primaryTags = tags
      .filter((tag) => !("primary" in tag) || tag.primary)
      .map((tag) => tag.name)
    const inactive = [STATUSES.DRAFT, STATUSES.CANCELED].includes(status)
    const canceled = status === STATUSES.CANCELED

    return html`
      <div
        class="assignment group filter-result-card ${inactive
          ? `inactive`
          : ``}"
      >
        <div
          class="-mx-8 px-8 -my-6 py-6 cursor-pointer"
          role="button"
          tabindex="0"
          @click=${(e) =>
            e?.target.tagName.toLowerCase() === "a"
              ? null
              : setExpanded(!expanded)}
          @keydown=${(e) => keyClickWrapper(e, () => setExpanded(!expanded))}
          aria-controls=${`item-${id}`}
          aria-expanded=${expanded.toString()}
        >
          ${HeaderSection(
            {
              status,
              tags: primaryTags,
              url,
              can_edit,
              can_clone,
              can_view: true,
              iconIcalSrc,
              iconGoogleCalendarSrc,
              gcal_link,
            },
            expanded
          )}
          <p
            class="title mb-5 text-lg text-blue group-hover:text-black font-bold"
          >
            ${name}
          </p>
          <div
            class="flex ${expanded
              ? "flex-col"
              : "flex-row"} flex-wrap leading-loose"
          >
            ${LocationInfo({ location, link, remote, canceled }, expanded)}
            ${DateTimeInfo(
              {
                date: start_date,
                start_time,
                end_time,
                shortened_timezone,
                canceled,
                agency,
              },
              expanded
            )}
          </div>
          ${status === STATUSES.OPEN && (can_edit || app_form)
            ? IconDetailBlock({ roles, categories, applications })
            : ``}
          ${expanded && description
            ? html`<p class="mt-4">
                ${markdown(
                  showFullDescription
                    ? description
                    : cliTruncate(description, 500),
                  description.length > 500
                    ? html`${showFullDescription
                        ? html`<a
                            href="javascript:void(0)"
                            @click=${() => setShowFullDescription(false)}
                            >Show less</a
                          >`
                        : html`<a
                            href="javascript:void(0)"
                            @click=${() => setShowFullDescription(true)}
                            >More</a
                          >`}`
                    : ""
                )}
              </p>`
            : ""}
        </div>
        <div
          class="collapse-row"
          id=${`item-${id}`}
          aria-hidden=${(!expanded).toString()}
        >
          ${expanded
            ? html`
                ${can_edit
                  ? html` ${RoleManagementSection({
                      url,
                      status,
                      roles,
                      categories,
                      applications,
                    })}`
                  : ``}
                ${app_form
                  ? html`${RoleApplySection({
                      app_form,
                      roles,
                      categories,
                      applications,
                    })}`
                  : ``}
              `
            : ``}
        </div>
      </div>
    `
  }
)

export default AssignmentRow
